import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CardContent,
  Typography,
  ListItem,
  Box,
  ListItemText,
  LinearProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAppContext } from '../context/AppContext';
import { financialService } from '../utils/api';
import CONFIG from '../utils/config';

const initCalendly = () => {
  if (window.Calendly) {
    window.Calendly.initPopupWidget({ url: CONFIG.calendly_link });
  }
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function presentValue(rate, nper, pmt, fv, type = 1) {
  if (rate === 0) return -((pmt * nper) + fv);
  return -(pmt * ((1 - Math.pow(1 + rate, -nper)) / rate) + fv / Math.pow(1 + rate, nper));
}

function numberValue(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function calculateFundingSnapshot(state) {
  const currentYear = new Date().getFullYear();
  const discountRate = CONFIG.EXPECTED_ROR[state.riskProfile] || CONFIG.EXPECTED_ROR.Balanced;

  const totalPVGoals = (state.goals || []).reduce((sum, goal) => {
    const targetYear = numberValue(goal.targetYear);
    const yearsToGoal = Math.max(0, targetYear - currentYear - 3);
    const shortTerm = Math.max(0, Math.min(3, targetYear - currentYear));
    const intermediatePV = presentValue(discountRate, yearsToGoal, 0, numberValue(goal.FV_goal), 0);
    const pv = presentValue(CONFIG.DEFAULT_DEBT_RETURN, shortTerm, 0, intermediatePV, 1);
    return sum + pv;
  }, 0);

  const totalPVEducation = (state.kidsEducation || []).reduce((sum, kid) => {
    const kidAge = numberValue(kid.Age);

    const yearsToUG = Math.max(0, CONFIG.DEFAULT_ug_AGE - kidAge - 3);
    const shortUG = Math.max(0, Math.min(3, CONFIG.DEFAULT_ug_AGE - kidAge));
    const interUGPV = presentValue(discountRate, yearsToUG, 0, numberValue(kid.ug_fees_FV), 0);
    const ugPV = presentValue(CONFIG.DEFAULT_DEBT_RETURN, shortUG, 0, interUGPV, 1);

    const yearsToPG = Math.max(0, CONFIG.DEFAULT_pg_AGE - kidAge - 3);
    const shortPG = Math.max(0, Math.min(3, CONFIG.DEFAULT_pg_AGE - kidAge));
    const interPGPV = presentValue(discountRate, yearsToPG, 0, numberValue(kid.pg_fees_FV), 0);
    const pgPV = presentValue(CONFIG.DEFAULT_DEBT_RETURN, shortPG, 0, interPGPV, 1);

    return sum + ugPV + pgPV;
  }, 0);

  const yearsForRetirement = Math.max(0, numberValue(state.yearsForRetirement));
  const amountRequiredToday =
    (numberValue(state.corpusRequired) / Math.pow(1 + discountRate, yearsForRetirement))
    + totalPVGoals
    + totalPVEducation
    + numberValue(state.emergencyFunds);

  const fundingScore = amountRequiredToday > 0
    ? Math.round((numberValue(state.netWorth) / amountRequiredToday) * 100)
    : 0;

  return {
    amountRequiredToday,
    fundingScore,
  };
}

function buildSubmitPayload(state, amountRequiredToday, fundingScore) {
  return {
    clientName:            state.clientName,
    emailId:               state.email,
    phoneNumber:           state.phoneNumber,
    birthYear:             state.birthYear,
    age:                   state.age,
    retirementAge:         state.retirementAge,
    yearlyExpenses:        state.monthlyExpensesInRetirement,
    corpusRequired:        state.corpusRequired,
    goals:                 state.goals || [],
    kidsEducation:         state.kidsEducation || [],
    riskProfile:           state.riskProfile,
    emergency_funds:       state.emergencyFunds,
    amountRequiredToday,
    currentInvestment:     state.netWorth,
    fundingScore,
    requiredRateOfReturn:  0,
    phoneNumberVerified:   true,
    city:                  state.city,
    receiveReport:         state.receiveReport,
    receiveGuide:          false,
    requestCallback:       state.requestCallback,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const FundingStatusCard = () => {
  const { state, actions } = useAppContext();
  const [infoDialogOpen, setInfoDialogOpen]   = useState(false);
  const [infoContent,    setInfoContent]      = useState('assumptions');
  const lastSubmittedKeyRef = useRef('');
  const { amountRequiredToday, fundingScore } = useMemo(
    () => calculateFundingSnapshot(state),
    [state],
  );
  const submitPayload = useMemo(
    () => buildSubmitPayload(state, amountRequiredToday, fundingScore),
    [state, amountRequiredToday, fundingScore],
  );
  const submitPayloadKey = useMemo(
    () => JSON.stringify(submitPayload),
    [submitPayload],
  );

  // ── Funding score calculation ──────────────────────────────────────────
  useEffect(() => {
    if (state.amountRequiredToday !== amountRequiredToday) {
      actions.updateFormData('amountRequiredToday', amountRequiredToday);
    }
    if (state.fundingScore !== fundingScore) {
      actions.updateFormData('fundingScore', fundingScore);
    }
  }, [amountRequiredToday, fundingScore]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Submit latest goal-planning data in the background ────────────────
  useEffect(() => {
    if (amountRequiredToday <= 0) return;
    if (!submitPayload.phoneNumber) return;
    if (lastSubmittedKeyRef.current === submitPayloadKey) return;

    const timeoutId = setTimeout(async () => {
      try {
        await financialService.submitRequest(submitPayload);
        lastSubmittedKeyRef.current = submitPayloadKey;
        actions.updateFormData('goalDataSubmitted', true);
        console.log('Goal-planning data submitted successfully.', {
          amountRequiredToday,
          fundingScore,
        });
      } catch (err) {
        console.error('submitRequest failed:', err);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [amountRequiredToday, fundingScore, submitPayload, submitPayloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Render helpers ────────────────────────────────────────────────────
  const score      = fundingScore;
  const progressValue = Math.max(0, Math.min(100, score));
  const firstName  = state.clientName?.split(' ')[0] || '';
  const scoreColor = score < 30 ? 'error' : score < 70 ? 'warning' : 'success';

  const getMessage = () => {
    if (score < 40) return {
      bold: `That's a start ${firstName}, a strong financial future takes time.`,
      body: `Consistent saving and systematic investing build momentum — however it's your`,
    };
    if (score < 60) return {
      bold: `You are about halfway there, ${firstName}.`,
      body: `Consistent saving and systematic investing build momentum — however it's your`,
    };
    if (score < 80) return {
      bold: `That's commendable progress, ${firstName}.`,
      body: `Consistent saving and systematic investing build momentum — but it's your`,
    };
    if (score < 100) return {
      bold: `You are almost there, ${firstName}! You've worked hard to get this far.`,
      body: `Consistent saving and systematic investing build momentum — however it's your`,
    };
    return {
      bold: `Congratulations, ${firstName}!! You've done it. You are Financially Independent.`,
      body: `But it's not over till it's truly over. Let's ensure that your`,
    };
  };

  const msg = getMessage();

  return (
    <Box>
      <CardContent>
        {/* Score display */}
        <ListItem>
          <ListItemText
            primary={
              <Box sx={{ mt: 1 }}>
                <Box>
                  <Typography variant="h4" align="center" fontWeight="bold" color={scoreColor}>
                    {score}%
                    <Button
                      variant="text"
                      color="primary"
                      startIcon={<InfoIcon />}
                      onClick={() => { setInfoContent('assumptions'); setInfoDialogOpen(true); }}
                      sx={{ mr: -4, textTransform: 'none', fontSize: '0.875rem' }}
                    />
                  </Typography>
                  <Typography variant="h6" align="center" sx={{ pl: 1 }}>
                    of your goals are secured
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  color={scoreColor}
                  sx={{ height: 15, borderRadius: 5 }}
                />
              </Box>
            }
          />
        </ListItem>

        {/* Contextual message */}
        <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
          {msg.bold}
        </Typography>
        {/* <Typography variant="body1" sx={{ mb: 2 }}>
          The next, and equally critical, step is aligning your{' '}
          <b
            style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => { setInfoContent('assetAllocation'); setInfoDialogOpen(true); }}
          >
            asset allocation
          </b>{' '}
          with these goals.
        </Typography> */}
	
	{state.netWorth >= 10000000 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={async () => {
              initCalendly();
              try {
                await financialService.requestCallback({
                  phoneNumber:     state.phoneNumber,
                  lastName:        state.clientName,
                  emailId:         state.email,
                  requestCallback: true,
                  scheduleMeeting: false,
                  fundingScoreCallClicked: true,
                });
              } catch (err) {
                console.error('requestCallback failed:', err);
              }
            }}
            sx={{ py: 1.5, px: 4, borderRadius: 2, fontWeight: 'bold' }}
          >
            Schedule a Meeting
          </Button>
        </Box>
	)}
        <Typography variant="body1" sx={{ mt: 4 }}>
          Click <b>"<u>NEXT</u>"</b> to enter few details and get a free 4-pager Financial Plan.
          {/* <b>Click NEXT</b> to fill your income, savings and current investments details or we can fill it together on a call. */}
        </Typography>


        {/* Primary CTA — advances to TransitionCard */}
        {/* <Button
          variant="contained"
          color="success"
          fullWidth
          size="large"
          onClick={actions.nextStep}
          sx={{ borderRadius: 1, py: 1.5, textTransform: 'none', fontWeight: 'bold' }}
        >
          WHAT TO DO NEXT?
        </Button> */}
        {/* <Button
          variant="contained"
          color="success"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={actions.nextStep}
          fullWidth
          sx={{
            borderRadius: 1,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 'bold',
          }}
        >
          Continue
        </Button> */}

      </CardContent>

      {/* Info Dialog */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          {infoContent === 'assumptions' ? (
            <Typography variant="body2" color="textSecondary">
              Based on your inputs and following assumptions:
              <br />Life Expectancy: {CONFIG.DEFAULT_LIFE_EXPECTANCY}
              <br />Annual Inflation: {CONFIG.DEFAULT_INFLATION_RATE_STRING}
              <br />Asset Allocation: {state.riskProfile}
              <br />Expected net Rate of Return: {CONFIG.EXPECTED_ROR_STRING[state.riskProfile]}
            </Typography>
          ) : (
            <Typography>
              It's not your stock selection or your market timing — but your Asset Allocation —
              that drives most of your returns and risk.
              <br /><br />
              Asset Allocation means diversifying your investments across various asset classes
              such as Indian equities, global equities, gold, debt and income assets — based on
              your goals, time horizon, and risk tolerance.
              <br /><br />
              It's the single most important decision that determines how your wealth grows and
              how much risk you take for it.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)} variant="contained">Got it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FundingStatusCard;


